import type { ReactNode } from "react";
import { cx } from "./ui";

/**
 * The company mark, set the way the marketing site sets it: condensed, upper
 * case, with the second half in the accent. Stacked here rather than run on one
 * line, because at display size the two halves carry more weight apart — and it
 * echoes the site's two-line headline with one accent word.
 */
export function Wordmark({ className }: { className?: string }) {
  return (
    <div className={cx("display text-ink", className)}>
      <span className="block">Service</span>
      <span className="block text-accent">Finder</span>
    </div>
  );
}

/**
 * The frame around the two screens anyone outside the app ever sees: signing in,
 * and accepting an invitation.
 *
 * A door with two sides. The left says whose building this is; the right is the
 * lock. On a phone there is no room for both side by side, so the brand becomes
 * a line above the form rather than a panel beside it — the alternative is a
 * hero that pushes the password field below the fold.
 */
export function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[1.1fr_minmax(0,30rem)]">
      <aside className="relative hidden flex-col justify-between overflow-hidden border-r border-hairline p-10 lg:flex xl:p-14">
        {/* One light source, anchored behind the mark. The marketing site uses
            exactly this gradient behind its headline. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -left-1/4 top-[-20%] h-[70vh] w-[120%] bg-[radial-gradient(60%_50%_at_30%_50%,rgba(79,140,255,0.16),transparent_70%)]"
        />

        <p className="relative kicker">ServiceFinder LLC</p>

        <div className="relative">
          <Wordmark className="text-[clamp(3.5rem,7vw,6rem)]" />
          <div className="mt-8 h-px w-16 bg-accent" />
          <p className="mt-8 max-w-[38ch] text-[0.9375rem] leading-relaxed text-muted">
            The control plane behind the software we build and run — licences, projects and
            what has been paid for them.
          </p>
        </div>

        <a
          href="https://www.servicefinderllc.com"
          className="relative font-mono text-[0.6875rem] uppercase tracking-[0.2em] text-dim transition-colors hover:text-muted"
        >
          servicefinderllc.com
        </a>
      </aside>

      {/* Gutters shrink rather than disappear on a narrow phone: a form flush
          against the bezel reads as a rendering fault, not as a design. */}
      <main className="flex min-h-dvh flex-col justify-center gap-8 px-5 py-12 sm:px-10 lg:px-12 lg:py-16">
        <div className="w-full max-w-[24rem] lg:mx-auto">{children}</div>
      </main>
    </div>
  );
}

/**
 * Stands in for the brand panel on a phone, where there is no panel. Small
 * enough to sit above a form without becoming the thing you deal with first.
 */
export function CompactWordmark() {
  return (
    <div className="flex items-baseline gap-2 lg:hidden">
      <span className="display text-2xl text-ink">
        Service<span className="text-accent">Finder</span>
      </span>
    </div>
  );
}
